import React, { useState, useEffect } from 'react';
import { FiX, FiDownload, FiUploadCloud, FiFileText, FiCheckCircle, FiAlertCircle, FiLoader, FiSearch, FiChevronDown } from 'react-icons/fi';
import toast from 'react-hot-toast';
import axios from 'axios';
import { importProductsApi } from '../../services/user';

interface ImportProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CATEGORIES = [
  { id: 'Grocery', label: 'Grocery', icon: '🛒', description: 'Packaged food, beverages & essentials' },
  { id: 'Fashion', label: 'Fashion', icon: '👗', description: 'Clothing, footwear & accessories' },
  { id: 'Home & Kitchen', label: 'Home & Kitchen', icon: '🏠', description: 'Furnishings, kitchenware & decor' }
];

const CATEGORY_HEADERS: Record<string, string[]> = {
  Grocery: [
    'productCode', 'productName', 'retailPrice', 'purchasePrice', 'GST_Percentage', 'productSubcategory',
    'quantity', 'maxAllowedQty', 'UOM', 'packQty', 'length(cm)', 'breadth(cm)', 'height(cm)', 'weight(gram)', 'isReturnable',
    'returnWindow', 'isVegetarian', 'manufacturerName', 'manufacturedDate', 'nutritionalInfo', 'additiveInfo', 'isCancellable', 'description', 'manufacturerOrPackerAddress',
    'commonOrGenericNameOfCommodity', 'monthYearOfManufacturePackingImport', 'importerFSSAILicenseNo', 'brandOwnerFSSAILicenseNo'
  ],
  Fashion: [
    'productCode', 'productName', 'retailPrice', 'purchasePrice', 'GST_Percentage', 'productSubcategory',
    'quantity', 'maxAllowedQty', 'UOM', 'packQty', 'length(cm)', 'breadth(cm)', 'height(cm)', 'weight(gram)', 'isReturnable',
    'returnWindow', 'manufacturerName', 'additiveInfo', 'manufacturedDate', 'isCancellable', 'description', 'manufacturerOrPackerAddress',
    'gender', 'colour', 'size', 'brand', 'sizeChart', 'fabric', 'colourName'
  ],
  'Home & Kitchen': [
    'productCode', 'productName', 'retailPrice', 'purchasePrice', 'GST_Percentage', 'productSubcategory',
    'quantity', 'maxAllowedQty', 'UOM', 'packQty', 'length(cm)', 'breadth(cm)', 'height(cm)', 'weight(gram)', 'isReturnable',
    'returnWindow', 'manufacturerName', 'additiveInfo', 'manufacturedDate', 'isCancellable', 'description', 'manufacturerOrPackerAddress',
    'material', 'colour', 'brand', 'colourName'
  ]
};

// Sample dummy rows for user guidance in sample download
const SAMPLE_ROWS: Record<string, Record<string, string>> = {
  Grocery: {
    productCode: 'GROC-001',
    productName: 'Organic Whole Wheat Atta 5kg',
    retailPrice: '299',
    purchasePrice: '240',
    GST_Percentage: '5',
    productSubcategory: 'Atta & Flours',
    quantity: '50',
    maxAllowedQty: '5',
    UOM: 'kg',
    packQty: '5',
    'length(cm)': '30',
    'breadth(cm)': '20',
    'height(cm)': '10',
    'weight(gram)': '5000',
    isReturnable: 'Yes',
    returnWindow: '7',
    isVegetarian: 'Yes',
    manufacturerName: 'Udyam Organics',
    manufacturedDate: '2025-01-15',
    nutritionalInfo: 'Energy 340kcal, Protein 12g',
    additiveInfo: 'No added preservatives',
    isCancellable: 'Yes',
    description: 'Fresh ground 100% whole wheat flour',
    manufacturerOrPackerAddress: 'Plot 12, Industrial Area, New Delhi',
    commonOrGenericNameOfCommodity: 'Wheat Flour',
    monthYearOfManufacturePackingImport: '01/2025',
    importerFSSAILicenseNo: '10019011000123',
    brandOwnerFSSAILicenseNo: '10019011000123'
  },
  Fashion: {
    productCode: 'FASH-001',
    productName: 'Men Cotton Casual Shirt',
    retailPrice: '999',
    purchasePrice: '650',
    GST_Percentage: '12',
    productSubcategory: 'Shirts',
    quantity: '30',
    maxAllowedQty: '3',
    UOM: 'Piece',
    packQty: '1',
    'length(cm)': '25',
    'breadth(cm)': '20',
    'height(cm)': '3',
    'weight(gram)': '350',
    isReturnable: 'Yes',
    returnWindow: '10',
    manufacturerName: 'Udyam Apparels',
    additiveInfo: '100% Breathable Cotton',
    manufacturedDate: '2025-02-01',
    isCancellable: 'Yes',
    description: 'Premium quality slim fit cotton shirt',
    manufacturerOrPackerAddress: 'Sector 5, Textile Zone, Jaipur',
    gender: 'Men',
    colour: 'Blue',
    size: 'L',
    brand: 'UdyamStyle',
    sizeChart: 'Standard Indian Sizing',
    fabric: 'Cotton',
    colourName: 'Navy Blue'
  },
  'Home & Kitchen': {
    productCode: 'HOME-001',
    productName: 'Stainless Steel Water Bottle 1L',
    retailPrice: '499',
    purchasePrice: '320',
    GST_Percentage: '18',
    productSubcategory: 'Drinkware',
    quantity: '100',
    maxAllowedQty: '10',
    UOM: 'Piece',
    packQty: '1',
    'length(cm)': '10',
    'breadth(cm)': '10',
    'height(cm)': '30',
    'weight(gram)': '450',
    isReturnable: 'Yes',
    returnWindow: '7',
    manufacturerName: 'Udyam Home Needs',
    additiveInfo: 'BPA Free, Vacuum Insulated',
    manufacturedDate: '2025-01-10',
    isCancellable: 'Yes',
    description: 'Double wall insulated stainless steel bottle keeps liquids hot/cold for 24h',
    manufacturerOrPackerAddress: 'Phase 2, GIDC, Ahmedabad',
    material: 'Stainless Steel',
    colour: 'Silver',
    brand: 'UdyamHome',
    colourName: 'Matte Silver'
  }
};

export const ImportProductModal: React.FC<ImportProductModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('Grocery');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState<boolean>(false);

  const [sellers, setSellers] = useState<any[]>([]);
  const [selectedSellerId, setSelectedSellerId] = useState<string>('');
  const [sellerSearchQuery, setSellerSearchQuery] = useState<string>('');
  const [isSellerDropdownOpen, setIsSellerDropdownOpen] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      const fetchSellers = async () => {
        try {
          const userId = localStorage.getItem('ud-user');
          if (!userId) return;
          const baseUrl = process.env.REACT_APP_API_URL || 'https://seller.udyamwell.in/api';
          const response = await axios.get(`${baseUrl}/dashboard/manufacturers?userId=${userId}`);

          if (Array.isArray(response.data) && response.data.length > 0 && response.data[0].email) {
            setSellers(response.data);
          }
        } catch (err) {
          console.error("Failed to load sellers", err);
        }
      };
      fetchSellers();
    } else {
      setSelectedFile(null);
      setErrorMessage(null);
      setSelectedSellerId('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDownloadSample = () => {
    let fileName = `sample_${selectedCategory.toLowerCase().replace(/[^a-z0-9]/g, '_')}_import.xls`;
    let templatePath = `/templates/Grocery_Import_Sample.xlsx`;

    if (selectedCategory === 'Fashion') {
      templatePath = `/templates/Fashion_Import_Sample.xlsx`;
    } else if (selectedCategory === 'Home & Kitchen' || selectedCategory === 'Home') {
      templatePath = `/templates/Home_Kitchen_Import_Sample.xlsx`;
    }

    // Attempt to download static template file from public folder if present
    fetch(templatePath)
      .then((res) => {
        if (res.ok) {
          return res.blob();
        }
        throw new Error('Template file not found');
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `sample_${selectedCategory.toLowerCase().replace(/[^a-z0-9]/g, '_')}_import.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success(`Downloaded sample file for ${selectedCategory}`);
      })
      .catch(() => {
        // Fallback: Generate structured Excel HTML document so Excel opens every column in separate cells cleanly
        const headers = CATEGORY_HEADERS[selectedCategory] || CATEGORY_HEADERS['Grocery'];
        const sampleRow = SAMPLE_ROWS[selectedCategory] || {};

        const headerCells = headers.map(h => `<th style="background-color:#16a34a;color:#ffffff;font-weight:bold;border:1px solid #d1d5db;padding:8px;text-align:left;">${h}</th>`).join('');
        const dataCells = headers.map(h => `<td style="border:1px solid #d1d5db;padding:8px;text-align:left;">${sampleRow[h] || ''}</td>`).join('');

        const excelHtml = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta http-equiv="content-type" content="text/plain; charset=UTF-8"/>
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Sheet1</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head>
<body>
<table>
  <thead>
    <tr>${headerCells}</tr>
  </thead>
  <tbody>
    <tr>${dataCells}</tr>
  </tbody>
</table>
</body>
</html>`;

        const blob = new Blob([excelHtml], { type: 'application/vnd.ms-excel' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success(`Downloaded Excel sample file for ${selectedCategory}`);
      });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setErrorMessage(null);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const validTypes = ['.csv', '.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (validTypes.includes(fileExtension)) {
        setSelectedFile(file);
        setErrorMessage(null);
      } else {
        setErrorMessage('Please upload a valid Excel (.xlsx, .xls) or CSV file.');
      }
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setErrorMessage('Please select a file to upload.');
      return;
    }

    try {
      setIsUploading(true);
      setErrorMessage(null);

      const response = await importProductsApi(selectedFile, selectedCategory, selectedSellerId);

      if (response.success !== false) {
        toast.success(response.message || 'Products imported successfully!');
        setSelectedFile(null);
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        setErrorMessage(response.message || 'Import failed. Please check your file format.');
      }
    } catch (err: any) {
      const apiErrorMsg = err?.response?.data?.message || err.message || 'Error uploading file';
      setErrorMessage(apiErrorMsg);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      setErrorMessage(null);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6 sm:p-8 transform transition-all duration-200">

        {/* Close Button */}
        <button
          onClick={handleClose}
          disabled={isUploading}
          className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
          aria-label="Close"
        >
          <FiX className="w-6 h-6" />
        </button>

        {/* Modal Header */}
        <div className="mb-6 text-center sm:text-left">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <span className="p-2 bg-green-100 text-green-700 rounded-lg">
              <FiUploadCloud className="w-6 h-6" />
            </span>
            Import Products
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Upload an Excel or CSV file to bulk import products into your catalog.
          </p>
        </div>

        {/* Step 1: Select Category */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-800 mb-2">
            1. Select Product Category <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => {
                  setSelectedCategory(cat.id);
                  setErrorMessage(null);
                }}
                className={`flex flex-col items-center p-3.5 border-2 rounded-xl text-center transition-all ${selectedCategory === cat.id
                  ? 'border-green-600 bg-green-50/70 shadow-sm text-green-900 font-semibold'
                  : 'border-gray-200 hover:border-gray-300 bg-white text-gray-700'
                  }`}
              >
                <span className="text-2xl mb-1">{cat.icon}</span>
                <span className="text-sm font-medium">{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Target Seller Dropdown (For Organisations only) */}
        {sellers.length > 0 && (
          <div className="mb-6 relative">
            <label className="block text-sm font-semibold text-gray-800 mb-2">
              Select Seller (Optional)
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsSellerDropdownOpen(!isSellerDropdownOpen)}
                className="w-full border border-gray-300 rounded-xl py-2.5 px-3 bg-white text-gray-700 shadow-sm flex items-center justify-between focus:ring-green-500 focus:border-green-500"
              >
                <span className="truncate">
                  {selectedSellerId
                    ? (() => {
                      const s = sellers.find(s => s._id === selectedSellerId);
                      return s ? `${s.name} (${s.email})` : 'Import directly to my Organisation Account';
                    })()
                    : 'Import directly to my Organisation Account'}
                </span>
                <FiChevronDown className="w-5 h-5 text-gray-400" />
              </button>

              {isSellerDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  <div className="p-2 sticky top-0 bg-white border-b border-gray-100">
                    <div className="relative">
                      <FiSearch className="absolute left-2.5 top-2.5 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search seller by name or email..."
                        value={sellerSearchQuery}
                        onChange={(e) => setSellerSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <ul className="py-1">
                    <li
                      className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm text-gray-700"
                      onClick={() => {
                        setSelectedSellerId('');
                        setIsSellerDropdownOpen(false);
                        setSellerSearchQuery('');
                      }}
                    >
                      Import directly to my Organisation Account
                    </li>
                    {sellers
                      .filter(s =>
                        s.name?.toLowerCase().includes(sellerSearchQuery.toLowerCase()) ||
                        s.email?.toLowerCase().includes(sellerSearchQuery.toLowerCase())
                      )
                      .map((s: any) => (
                        <li
                          key={s._id}
                          className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm text-gray-700 border-t border-gray-50"
                          onClick={() => {
                            setSelectedSellerId(s._id);
                            setIsSellerDropdownOpen(false);
                            setSellerSearchQuery('');
                          }}
                        >
                          <div className="font-medium text-gray-900">{s.name}</div>
                          <div className="text-xs text-gray-500">{s.email}</div>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1.5 ml-1">If selected, the imported products will be assigned directly to this seller.</p>
          </div>
        )}

        {/* Step 2: Download Sample Template */}
        <div className="mb-6 bg-slate-50 p-4 rounded-xl border border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 text-blue-700 rounded-lg shrink-0">
              <FiFileText className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900">
                Sample Excel Template
              </h4>
              <p className="text-xs text-gray-500">
                Headers required for {selectedCategory}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDownloadSample}
            className="w-full sm:w-auto px-4 py-2 text-xs font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-1.5 shrink-0 shadow-sm"
          >
            <FiDownload className="w-4 h-4" />
            Download Sample Excel
          </button>
        </div>

        {/* Step 3: Upload File */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-800 mb-2">
            2. Upload Product File <span className="text-red-500">*</span>
          </label>

          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${dragActive
              ? 'border-green-500 bg-green-50'
              : selectedFile
                ? 'border-green-400 bg-green-50/30'
                : 'border-gray-300 hover:border-gray-400 bg-gray-50/50'
              }`}
          >
            <input
              type="file"
              id="product-file-input"
              accept=".csv, .xlsx, .xls"
              onChange={handleFileChange}
              className="hidden"
            />

            {selectedFile ? (
              <div className="flex flex-col items-center">
                <FiCheckCircle className="w-10 h-10 text-green-600 mb-2" />
                <span className="text-sm font-semibold text-gray-900 break-all">
                  {selectedFile.name}
                </span>
                <span className="text-xs text-gray-500 mt-0.5">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedFile(null)}
                  className="mt-3 text-xs font-medium text-red-600 hover:text-red-800 underline"
                >
                  Remove & choose another
                </button>
              </div>
            ) : (
              <label
                htmlFor="product-file-input"
                className="cursor-pointer flex flex-col items-center justify-center"
              >
                <FiUploadCloud className="w-10 h-10 text-gray-400 mb-2" />
                <span className="text-sm font-semibold text-gray-700">
                  Click to upload <span className="font-normal text-gray-500">or drag and drop</span>
                </span>
                <span className="text-xs text-gray-400 mt-1">
                  Supported formats: .xlsx, .xls, .csv
                </span>
              </label>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {errorMessage && (
          <div className="mb-6 p-3.5 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs flex items-start gap-2">
            <FiAlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1">{errorMessage}</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={isUploading}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isUploading || !selectedFile}
            className="px-6 py-2.5 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 active:bg-green-800 rounded-xl shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isUploading ? (
              <>
                <FiLoader className="w-4 h-4 animate-spin" />
                Importing...
              </>
            ) : (
              'Upload & Import'
            )}
          </button>
        </div>

      </div>
    </div>
  );
};
